import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection, type useRxData } from "rxdb-hooks";
import { SYNCABLE_META } from "./common";
import { EXAM_TYPES } from "./epocus-exam-worksheets";

export const RXDB_EPOCUS_PROGRAM_COLLECTION_NAME = "epocus-program";

export const useRxCollectionEpocusProgram =
  (): RxCollection<EpocusProgram> | null =>
    useRxCollection<EpocusProgram>(RXDB_EPOCUS_PROGRAM_COLLECTION_NAME);

type UseRxData = Parameters<typeof useRxData<EpocusProgram>>;
export type EpocusProgramQueryConstructor = UseRxData["1"];

export type EpocusProgramStatus = "active" | "disabled";

export const WORKSHEET_SETS = ["standard", "us"] as const;

export type WorksheetSet = (typeof WORKSHEET_SETS)[number];

export const PROGRAM_CLINICAL_SETTINGS = [
  "office",
  "hospital_outpatient",
  "ed",
  "asc",
] as const;

export type ProgramClinicalSetting = (typeof PROGRAM_CLINICAL_SETTINGS)[number];

export const BILLING_MODIFIER_OVERRIDES = ["none", "mod_26"] as const;

export type BillingModifierOverride =
  (typeof BILLING_MODIFIER_OVERRIDES)[number];

const EPOCUS_PROGRAM_SCHEMA_LITERAL = {
  title: "epocus program schema",
  version: 9,
  description: "An ePOCUS program the signed-in user is a member of",
  type: "object",
  primaryKey: "id",
  properties: {
    id: {
      type: "string",
      maxLength: 100,
    },
    name: {
      type: "string",
      maxLength: 200,
    },
    status: {
      type: "string",
      maxLength: 20,
      enum: ["active", "disabled"],
    },
    elephantFacilityId: {
      type: ["string", "null"],
    },
    dimseAeTitle: {
      type: ["string", "null"],
      maxLength: 16,
    },
    enabledExamTypes: {
      type: "array",
      items: {
        type: "string",
        enum: EXAM_TYPES,
      },
    },
    worksheetSet: {
      type: "string",
      maxLength: 20,
      enum: WORKSHEET_SETS,
    },
    clinicalSetting: {
      type: "string",
      maxLength: 30,
      enum: PROGRAM_CLINICAL_SETTINGS,
    },
    billingModifierOverride: {
      type: ["string", "null"],
      enum: [...BILLING_MODIFIER_OVERRIDES, null],
    },
    enabledDeviceIds: {
      type: "array",
      items: {
        type: "string",
      },
    },
    turnaroundTargetMinutes: {
      type: ["number", "null"],
    },
    meta: SYNCABLE_META,
  },
  required: [
    "id",
    "name",
    "status",
    "enabledExamTypes",
    "enabledDeviceIds",
    "clinicalSetting",
    "billingModifierOverride",
    "meta",
  ],
  indexes: [["name"]],

  /*
   * It is important to set 'as const' to preserve the literal type.
   *
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;

const schemaTyped = toTypedRxJsonSchema(EPOCUS_PROGRAM_SCHEMA_LITERAL);

export type EpocusProgram = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

export const EPOCUS_PROGRAM_SCHEMA: RxJsonSchema<EpocusProgram> =
  EPOCUS_PROGRAM_SCHEMA_LITERAL;
